import { token } from '@/infrastructure/container';
import { type IDomLocale } from '@/services/DomLocale';
import { type ILogger } from '@/services/Logger';
import {
  type Locale,
  type LazyLocale,
  type MessageKey,
  type Messages,
  en,
  interpolate,
  resolveLocale,
} from './messages';

export interface II18n {
  readonly locale: Locale;
  /** Load the browser-preferred locale's messages. Awaited once at boot, before first render. */
  init(): Promise<void>;
  /** Translate a key, substituting `{name}` placeholders. Falls back to English, then the key. */
  t(key: MessageKey, vars?: Record<string, string | number>): string;
}

export const TOKEN_II18n = token<II18n>('II18n');

// English ships in the main bundle; every other locale is its own lazily-imported chunk, so a user
// only ever downloads the one language they need.
async function loadLocale(code: LazyLocale): Promise<Messages> {
  switch (code) {
    case 'fr':
      return (await import('./fr')).fr;
    case 'es':
      return (await import('./es')).es;
    case 'de':
      return (await import('./de')).de;
    case 'zh':
      return (await import('./zh')).zh;
  }
}

export class I18n implements II18n {
  private _locale: Locale = 'en';
  private _messages: Messages = en;

  constructor(
    private domLocale: IDomLocale,
    private logger: ILogger,
  ) {}

  get locale(): Locale {
    return this._locale;
  }

  async init(): Promise<void> {
    const code = resolveLocale(this.domLocale.language());
    if (code === 'en') return;
    try {
      this._messages = await loadLocale(code);
      this._locale = code;
    } catch (error) {
      this.logger.warn('i18n: failed to load locale, falling back to English', code, error);
    }
  }

  t(key: MessageKey, vars?: Record<string, string | number>): string {
    return interpolate(this._messages[key] ?? en[key] ?? key, vars);
  }
}

/** Test double: always English, no async loading. */
export class FakeI18n implements II18n {
  readonly locale: Locale = 'en';
  init(): Promise<void> {
    return Promise.resolve();
  }
  t(key: MessageKey, vars?: Record<string, string | number>): string {
    return interpolate(en[key] ?? key, vars);
  }
}
